import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  const [impersonation, setImpersonation] = useState(() => {
    const raw = localStorage.getItem('impersonation');
    return raw ? JSON.parse(raw) : null;
  });

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          setUser(response.data);
          setAccessToken(token);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { access_token, refresh_token, user: userData } = response.data;

      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.removeItem('impersonation');
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
      setUser(userData);
      setImpersonation(null);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      return { success: false, error: message };
    }
  };

  // Platform-admin passwordless login: request an emailed code, then verify.
  // verify returns the exact LoginResponse shape (login's twin).
  // Email-code (passwordless) login — works for all users in `users`
  // (operators + platform). Generalized backend endpoints.
  const requestCode = async (email) => {
    try {
      await axios.post(`${API_URL}/api/auth/login/request-code`, { email });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Could not send code. Please try again.';
      return { success: false, error: message };
    }
  };

  const verifyCode = async (email, code) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/verify-code`, { email, code });
      const { access_token, refresh_token, user: userData } = response.data;

      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.removeItem('impersonation');
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
      setUser(userData);
      setImpersonation(null);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired code.';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      if (accessToken) {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
      }
    } catch (error) {
      // Continue with local logout
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentFacilityId');
    localStorage.removeItem('impersonation');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setImpersonation(null);
  };

  const refreshAccessToken = async () => {
    try {
      const stored = localStorage.getItem('refreshToken');
      if (!stored) throw new Error('No refresh token');

      const response = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: stored });
      const { access_token, refresh_token: rotated } = response.data;
      localStorage.setItem('accessToken', access_token);
      // Sliding session: keep the re-issued refresh token so an active
      // device never ages out of its sign-in.
      if (rotated) {
        localStorage.setItem('refreshToken', rotated);
        setRefreshToken(rotated);
      }
      setAccessToken(access_token);
      return access_token;
    } catch (error) {
      logout();
      throw error;
    }
  };

  const startImpersonation = async (organizationId, reason) => {
    const res = await axios.post(
      `${API_URL}/api/platform/impersonate`,
      { organization_id: organizationId, reason: reason || null },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const { access_token, organization_name } = res.data;
    const session = { organization_id: organizationId, organization_name };
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('impersonation', JSON.stringify(session));
    localStorage.removeItem('currentFacilityId');
    setAccessToken(access_token);
    setImpersonation(session);
    return session;
  };

  const exitImpersonation = async () => {
    const res = await axios.post(
      `${API_URL}/api/platform/impersonate/exit`,
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const { access_token } = res.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.removeItem('impersonation');
    localStorage.removeItem('currentFacilityId');
    setAccessToken(access_token);
    setImpersonation(null);
  };

  const isAuthenticated = () => !!user && !!accessToken;
  const isPlatformAdmin = () => user?.platform_role === 'platform_admin';
  const isPlatformSupport = () => user?.platform_role === 'platform_support';
  const isPlatformUser = () => !!user?.platform_role;
  const isPortfolioOwner = () =>
    user?.role === 'portfolio_owner' ||
    (Array.isArray(user?.roles) && user.roles.includes('portfolio_owner'));
  // Portfolio-scoped = owner OR manager of the whole organization. Mirrors
  // the backend's User.is_portfolio_scoped(). These roles see every facility
  // in the org, so org-wide controls (e.g. facility dropdown ordering) gate
  // on this rather than ownership alone.
  const isPortfolioScoped = () =>
    user?.role === 'portfolio_owner' ||
    user?.role === 'portfolio_manager' ||
    (Array.isArray(user?.roles) &&
      (user.roles.includes('portfolio_owner') ||
        user.roles.includes('portfolio_manager')));
  const isFacilityOwner = () => user?.role === 'facility_owner';
  const isOwner = () => user?.role === 'portfolio_owner' || user?.role === 'facility_owner';
  const isManager = () =>
    isOwner() ||
    user?.role === 'portfolio_manager' ||
    user?.role === 'facility_manager' ||
    isPlatformUser();

  return (
    <AuthContext.Provider
      value={{
        user, accessToken, refreshToken, loading,
        impersonation,
        login, requestCode, verifyCode, logout, refreshAccessToken,
        startImpersonation, exitImpersonation,
        isAuthenticated,
        isPlatformAdmin, isPlatformSupport, isPlatformUser,
        isPortfolioOwner, isPortfolioScoped, isFacilityOwner,
        isOwner, isManager,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
